/**
 * Staff delivery queue (Story 8-2, 8-3) and Quick Sale (Story 8-4).
 * Fetches assigned deliveries, renders cards with maps/tel links, expandable details.
 * Mark Delivered and Report Issue actions per card.
 * Quick Sale: on-the-spot order creation with Walk-in or existing customer.
 */
import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Card } from "../../components/molecules/Card";
import { Button } from "../../components/atoms/Button";
import { Alert } from "../../components/atoms/Alert";
import { Modal } from "../../components/molecules/Modal";
import { ProductPhoto } from "../../components/catalog/ProductPhoto";
import { CustomerForm } from "../../components/organisms/CustomerForm";
import {
  listStaffDeliveries,
  completeDelivery,
  reportIssue,
  type StaffDeliveryItem,
  type StaffDeliveryIssueReason,
} from "../../services/staffDeliveriesService";
import {
  listStaffProducts,
  createQuickSale,
  listMyQuickSalesToday,
  type StaffProductItem,
  type QuickSaleListItem,
  type QuickSaleCreatePayload,
} from "../../services/staffQuickSaleService";
import { getCustomers, createCustomer } from "../../services/customerService";
import { UnpaidOrdersTab } from "./UnpaidOrdersTab";
import type { Customer, CreateCustomerData, OrderDiscountScope, OrderDiscountType } from "../../types/models";
import { OrderDiscountControls } from "../../components/organisms/OrderDiscountControls";
import { UnitPriceInput, LineDiscountControl, PriceUnitHint, createOrderLineKey } from "../../components/organisms/OrderLineItems";
import {
  computeDisplayedOrderTotals,
  quantizeMoney01,
  type OrderFormDiscountType,
} from "../../utils/orderTotals";
import { getGoogleMapsUrl } from "../../utils/maps";
import { formatCurrency } from "../../utils/currency";
import { formatDecimal } from "../../utils/numberFormat";
import { formatTimeAgo } from "../../utils/date";
import { getApiErrorMessage } from "../../utils/apiError";
import {
  MapPinIcon,
  PhoneIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShoppingCartIcon,
  MinusIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

export function StaffView() {
  const { t } = useTranslation("staff");
  const location = useLocation();
  const accessDenied = (location.state as { accessDenied?: boolean } | null)?.accessDenied;

  const [items, setItems] = useState<StaffDeliveryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmDelivery, setConfirmDelivery] = useState<{
    id: string;
    order_number: string;
    customer_name: string;
  } | null>(null);
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [reportIssueOrder, setReportIssueOrder] = useState<StaffDeliveryItem | null>(null);
  const [reportIssueReason, setReportIssueReason] =
    useState<StaffDeliveryIssueReason>("customer_not_available");
  const [reportIssueNotes, setReportIssueNotes] = useState("");
  const [submittingIssue, setSubmittingIssue] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  // Quick Sale (Story 8-4)
  const [quickSaleOpen, setQuickSaleOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [products, setProducts] = useState<StaffProductItem[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  type QtyMode = "units" | "packs";
  type QuickSaleLine = {
    lineKey: string;
    catalog_item_id: string;
    product_name: string;
    unit_price: number;
    quantity: number;
    unit_of_measure?: string | null;
    sales_pack_sizes?: number[] | null;
    selectedPackSize?: number;
    qtyMode?: QtyMode;
    primary_photo_id?: string | null;
    discount_type?: OrderFormDiscountType | null;
    discount_value?: number | null;
  };
  const [quickSaleLines, setQuickSaleLines] = useState<QuickSaleLine[]>([]);
  // Story 11.4-1: quick-sale discount mode + whole-order discount.
  const [qsDiscountScope, setQsDiscountScope] = useState<OrderDiscountScope>("none");
  const [qsOrderDiscountType, setQsOrderDiscountType] = useState<OrderFormDiscountType>("none");
  const [qsOrderDiscountValue, setQsOrderDiscountValue] = useState(0);
  const [qsDiscountReason, setQsDiscountReason] = useState("");
  const [lastSelectedProductId, setLastSelectedProductId] = useState<string | null>(null);
  // Story 11.6-1: opt-in deferred payment on quick sale (requires a real customer).
  const [qsDeferred, setQsDeferred] = useState(false);
  const [qsPaymentDueDate, setQsPaymentDueDate] = useState("");
  const [quickSaleNotes, setQuickSaleNotes] = useState("");
  const [quickSaleSubmitting, setQuickSaleSubmitting] = useState(false);
  const [quickSalesToday, setQuickSalesToday] = useState<QuickSaleListItem[]>([]);
  const [quickSalesTodayLoading, setQuickSalesTodayLoading] = useState(false);
  // Story 11.6-3: third "Impayés" tab (company-wide unpaid orders).
  const [activeTab, setActiveTab] = useState<"deliveries" | "sales" | "unpaid">("deliveries");
  const [showNoCustomerWarning, setShowNoCustomerWarning] = useState(false);
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listStaffDeliveries();
      setItems(res.items);
    } catch (e) {
      setError(getApiErrorMessage(e, t("error_load_deliveries")));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (accessDenied) return;
    fetchDeliveries();
  }, [accessDenied, fetchDeliveries]);

  const handleRefresh = () => {
    fetchDeliveries();
  };

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const showToastMessage = (message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleMarkDeliveredClick = (order: StaffDeliveryItem) => {
    setConfirmDelivery({
      id: order.id,
      order_number: order.order_number,
      customer_name: order.customer_name,
    });
    setDeliveryNotes("");
  };

  const handleConfirmDelivery = async () => {
    if (!confirmDelivery) return;
    setCompletingId(confirmDelivery.id);
    setError(null);
    try {
      await completeDelivery(confirmDelivery.id, deliveryNotes.trim() || undefined);
      setItems((prev) => prev.filter((i) => i.id !== confirmDelivery.id));
      setConfirmDelivery(null);
      setDeliveryNotes("");
      showToastMessage(t("toast_delivery_confirmed"), "success");
    } catch (e) {
      showToastMessage(getApiErrorMessage(e, t("error_mark_delivered")), "error");
    } finally {
      setCompletingId(null);
    }
  };

  const handleReportIssueClick = (order: StaffDeliveryItem) => {
    setReportIssueOrder(order);
    setReportIssueReason("customer_not_available");
    setReportIssueNotes("");
  };

  const handleSubmitReportIssue = async () => {
    if (!reportIssueOrder) return;
    setSubmittingIssue(true);
    setError(null);
    try {
      await reportIssue(reportIssueOrder.id, reportIssueReason, reportIssueNotes || undefined);
      setReportIssueOrder(null);
      showToastMessage(t("toast_issue_reported"), "success");
    } catch (e) {
      showToastMessage(getApiErrorMessage(e, t("error_report_issue")), "error");
    } finally {
      setSubmittingIssue(false);
    }
  };

  const fetchQuickSalesToday = useCallback(async () => {
    setQuickSalesTodayLoading(true);
    try {
      const res = await listMyQuickSalesToday(50);
      setQuickSalesToday(res.items);
    } catch (e) {
      setQuickSalesToday([]);
    } finally {
      setQuickSalesTodayLoading(false);
    }
  }, []);

  useEffect(() => {
    if (accessDenied) return;
    if (activeTab === "sales") fetchQuickSalesToday();
  }, [accessDenied, activeTab, fetchQuickSalesToday]);

  // Load customers and products when Quick Sale modal opens
  useEffect(() => {
    if (!quickSaleOpen) return;
    setProductsLoading(true);
    listStaffProducts({ limit: 100 })
      .then((r) => setProducts(r.items))
      .catch(() => setProducts([]))
      .finally(() => setProductsLoading(false));
  }, [quickSaleOpen]);

  // Pre-load customers when dropdown opens (even without search)
  useEffect(() => {
    if (customerDropdownOpen && customers.length === 0 && !customersLoading) {
      setCustomersLoading(true);
      getCustomers({ limit: 100 })
        .then((r) => setCustomers(r.items))
        .catch(() => setCustomers([]))
        .finally(() => setCustomersLoading(false));
    }
  }, [customerDropdownOpen, customers.length]);

  // Story 11.4-1: live totals mirror the backend pricing engine.
  const quickSaleTotals = computeDisplayedOrderTotals(
    quickSaleLines,
    qsDiscountScope,
    qsOrderDiscountType,
    qsOrderDiscountValue
  );
  const quickSaleTotal = quickSaleTotals.total;

  const setQuickSaleUnitPrice = (lineKey: string, value: number) => {
    setQuickSaleLines((prev) =>
      prev.map((l) => (l.lineKey === lineKey ? { ...l, unit_price: Math.max(0, value) } : l))
    );
  };
  const setQuickSaleLineDiscountType = (lineKey: string, type: OrderFormDiscountType) => {
    setQuickSaleLines((prev) =>
      prev.map((l) =>
        l.lineKey === lineKey
          ? {
              ...l,
              discount_type: type === "none" ? null : type,
              discount_value: type === "none" ? null : (l.discount_value ?? 0),
            }
          : l
      )
    );
  };
  const setQuickSaleLineDiscountValue = (lineKey: string, value: number) => {
    setQuickSaleLines((prev) =>
      prev.map((l) => (l.lineKey === lineKey ? { ...l, discount_value: Math.max(0, value) } : l))
    );
  };
  const handleQsScopeChange = (scope: OrderDiscountScope) => {
    setQsDiscountScope(scope);
    if (scope !== "order") {
      setQsOrderDiscountType("none");
      setQsOrderDiscountValue(0);
      setQsDiscountReason("");
    }
    if (scope !== "line") {
      setQuickSaleLines((prev) => prev.map((l) => ({ ...l, discount_type: null, discount_value: null })));
    }
  };

  /** Build the quick-sale items payload with custom price snapshot + per-line discount (Story 11.4-1). */
  const buildQuickSaleItems = () =>
    quickSaleLines
      .filter((l) => l.quantity > 0)
      .map((l) => {
        const item: {
          catalog_item_id: string;
          quantity: number;
          unit_price?: number;
          discount_type?: OrderDiscountType;
          discount_value?: number;
        } = {
          catalog_item_id: l.catalog_item_id,
          quantity: l.quantity,
          unit_price: quantizeMoney01(l.unit_price),
        };
        if (
          qsDiscountScope === "line" &&
          l.discount_type &&
          l.discount_type !== "none" &&
          Number(l.discount_value) > 0
        ) {
          item.discount_type = l.discount_type;
          item.discount_value = Number(l.discount_value);
        }
        return item;
      });

  const addQuickSaleLine = (product: StaffProductItem, addPacks?: number, packSize?: number) => {
    const sizes = product.sales_pack_sizes ?? [];
    const effectiveSize = packSize ?? sizes[0] ?? 0;
    const toAdd =
      addPacks != null && addPacks >= 1 && effectiveSize >= 1 ? addPacks * effectiveSize : 1;
    setLastSelectedProductId(product.id);
    const matches = quickSaleLines.filter((l) => l.catalog_item_id === product.id);
    if (matches.length === 1) {
      const existingKey = matches[0].lineKey;
      setQuickSaleLines((prev) =>
        prev.map((l) =>
          l.lineKey === existingKey
            ? {
                ...l,
                quantity:
                  addPacks != null && effectiveSize >= 1
                    ? Math.max(0, Math.floor(l.quantity / effectiveSize)) * effectiveSize + toAdd
                    : l.quantity + toAdd,
              }
            : l
        )
      );
    } else {
      setQuickSaleLines((prev) => [
        ...prev,
        {
          lineKey: createOrderLineKey(),
          catalog_item_id: product.id,
          product_name: product.name,
          unit_price: product.unit_price,
          quantity: toAdd,
          unit_of_measure: product.unit_of_measure ?? product.unit ?? null,
          sales_pack_sizes: sizes.length >= 1 ? sizes : undefined,
          selectedPackSize: sizes.length >= 1 ? (packSize ?? sizes[0]) : undefined,
          qtyMode: sizes.length >= 1 ? "units" : undefined,
          primary_photo_id: product.primary_photo_id ?? null,
        },
      ]);
    }
  };

  const setQuickSaleQtyMode = (lineKey: string, mode: QtyMode) => {
    setQuickSaleLines((prev) =>
      prev.map((l) => {
        if (l.lineKey !== lineKey) return l;
        if (mode !== "packs") return { ...l, qtyMode: mode };

        const size = l.selectedPackSize ?? l.sales_pack_sizes?.[0] ?? 1;
        if (size <= 1) return { ...l, qtyMode: mode };

        // Normalize units to an exact pack multiple when entering packs mode.
        // This avoids phantom extra bottles (e.g. 13 instead of 12 for 2 packs of 6).
        const normalizedPacks = Math.max(1, Math.floor(l.quantity / size));
        return {
          ...l,
          qtyMode: mode,
          quantity: normalizedPacks * size,
        };
      })
    );
  };

  const setQuickSalePackSize = (lineKey: string, size: number) => {
    setQuickSaleLines((prev) =>
      prev.map((l) => {
        if (l.lineKey !== lineKey) return l;
        if ((l.qtyMode ?? "units") !== "packs") {
          return { ...l, selectedPackSize: size };
        }

        const previousSize = l.selectedPackSize ?? l.sales_pack_sizes?.[0] ?? size;
        const currentPacks = Math.max(1, Math.floor(l.quantity / Math.max(1, previousSize)));
        return {
          ...l,
          selectedPackSize: size,
          quantity: currentPacks * size,
        };
      })
    );
  };

  const adjustQuickSaleQuantity = (lineKey: string, delta: number) => {
    setQuickSaleLines((prev) =>
      prev
        .map((l) =>
          l.lineKey === lineKey ? { ...l, quantity: Math.max(0, l.quantity + delta) } : l
        )
        .filter((l) => l.quantity > 0)
    );
  };

  const setQuickSaleQuantity = (lineKey: string, quantity: number) => {
    setQuickSaleLines((prev) =>
      prev
        .map((l) => (l.lineKey === lineKey ? { ...l, quantity: Math.max(1, quantity) } : l))
        .filter((l) => l.quantity > 0)
    );
  };

  const removeQuickSaleLine = (lineKey: string) => {
    setQuickSaleLines((prev) => prev.filter((l) => l.lineKey !== lineKey));
  };

  const resetQuickSaleDiscounts = () => {
    setQsDiscountScope("none");
    setQsOrderDiscountType("none");
    setQsOrderDiscountValue(0);
    setQsDiscountReason("");
  };

  const handleCompleteQuickSale = async () => {
    const items = buildQuickSaleItems();
    if (items.length === 0) {
      showToastMessage(t("toast_add_product"), "error");
      return;
    }
    // Story 11.6-1: a deferred (credit) sale requires a real customer — no credit to walk-in.
    if (qsDeferred && !selectedCustomer) {
      showToastMessage(t("quick_sale_deferred_requires_customer"), "error");
      return;
    }
    // Show warning if no customer selected
    if (!selectedCustomer) {
      setShowNoCustomerWarning(true);
      return;
    }
    await submitQuickSale(items);
  };

  const submitQuickSale = async (items: ReturnType<typeof buildQuickSaleItems>) => {
    setQuickSaleSubmitting(true);
    setError(null);
    try {
      const payload: QuickSaleCreatePayload = {
        customer_id: selectedCustomer?.id || null,
        items,
        notes: quickSaleNotes.trim() || undefined,
        discount_scope: qsDiscountScope,
      };
      if (qsDiscountScope === "order" && qsOrderDiscountType !== "none" && qsOrderDiscountValue > 0) {
        payload.discount_type = qsOrderDiscountType;
        payload.discount_value = qsOrderDiscountValue;
        if (qsDiscountReason.trim()) payload.discount_reason = qsDiscountReason.trim();
      }
      // Story 11.6-1: deferred payment (guarded above to require a real customer).
      if (qsDeferred) {
        payload.deferred = true;
        if (qsPaymentDueDate) payload.payment_due_date = qsPaymentDueDate;
      }
      const res = await createQuickSale(payload);
      showToastMessage(
        t("toast_sale_recorded", { amount: formatCurrency(Number(res.total_amount)) }),
        "success"
      );
      setQuickSaleLines([]);
      setQuickSaleNotes("");
      resetQuickSaleDiscounts();
      setQsDeferred(false);
      setQsPaymentDueDate("");
      setSelectedCustomer(null);
      setShowNoCustomerWarning(false);
      setQuickSaleOpen(false);
      fetchQuickSalesToday();
    } catch (e) {
      showToastMessage(getApiErrorMessage(e, t("toast_sale_failed")), "error");
    } finally {
      setQuickSaleSubmitting(false);
    }
  };

  const openQuickSale = () => {
    setQuickSaleLines([]);
    setQuickSaleNotes("");
    resetQuickSaleDiscounts();
    setQsDeferred(false);
    setQsPaymentDueDate("");
    setSelectedCustomer(null);
    setCustomerSearch("");
    setCustomerDropdownOpen(false);
    setShowNoCustomerWarning(false);
    setShowCreateCustomer(false);
    setQuickSaleOpen(true);
  };

  const handleCreateCustomer = async (data: CreateCustomerData) => {
    setCreatingCustomer(true);
    try {
      const result = await createCustomer(data);
      showToastMessage(t("toast_customer_created", { name: result.name }), "success");
      // Refresh customer list
      const updatedCustomers = await getCustomers({ limit: 100 });
      setCustomers(updatedCustomers.items);
      // Auto-select the newly created customer
      setSelectedCustomer(result);
      setShowCreateCustomer(false);
    } catch (e) {
      showToastMessage(getApiErrorMessage(e, t("toast_customer_create_failed")), "error");
      // Re-throw so CustomerForm can show the backend error inline.
      throw e;
    } finally {
      setCreatingCustomer(false);
    }
  };

  if (accessDenied) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="mb-4 max-w-md w-full">
          <Alert variant="error">{t("access_denied")}</Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto pb-24">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Staff</h1>
        <div className="flex items-center gap-2">
          <nav
            className="flex rounded-lg border border-neutral-200 dark:border-neutral-700 p-0.5"
            aria-label="Tabs"
          >
            <button
              type="button"
              onClick={() => setActiveTab("deliveries")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md min-h-[44px] ${
                activeTab === "deliveries"
                  ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                  : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
              }`}
            >
              {t("tab_deliveries")}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("sales")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md min-h-[44px] ${
                activeTab === "sales"
                  ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                  : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
              }`}
            >
              {t("tab_sales")}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("unpaid")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md min-h-[44px] ${
                activeTab === "unpaid"
                  ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                  : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
              }`}
            >
              {t("tab_unpaid")}
            </button>
          </nav>
          {activeTab === "deliveries" && (
            <Button
              type="button"
              variant="secondary"
              onClick={handleRefresh}
              disabled={loading}
              className="inline-flex items-center gap-2"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} aria-hidden />
              {t("btn_refresh")}
            </Button>
          )}
        </div>
      </div>

      {activeTab === "deliveries" && loading && items.length === 0 ? (
        <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-8 text-center">
          <p className="text-neutral-600 dark:text-neutral-400">{t("deliveries_loading")}</p>
        </div>
      ) : activeTab === "deliveries" && error ? (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-red-800 dark:text-red-200">
          {error}
        </div>
      ) : activeTab === "deliveries" && items.length === 0 ? (
        <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-8 text-center flex flex-col gap-4">
          <p className="text-neutral-600 dark:text-neutral-400 font-medium">
            {t("deliveries_empty")}
          </p>
          <p className="text-sm text-neutral-500 dark:text-neutral-500">
            {t("deliveries_empty_hint")}
          </p>
        </div>
      ) : activeTab === "deliveries" ? (
        <div className="space-y-4">
          {items.map((order) => {
            const effectiveAddress =
              order.delivery_address?.trim() || order.customer_address?.trim() || null;
            const effectiveAddressNotes = order.customer_location_note?.trim() || null;
            const effectiveLatitude = order.delivery_latitude ?? order.customer_latitude ?? null;
            const effectiveLongitude = order.delivery_longitude ?? order.customer_longitude ?? null;
            const mapsUrl = getGoogleMapsUrl({
              latitude: effectiveLatitude,
              longitude: effectiveLongitude,
              address: effectiveAddress,
            });
            const isExpanded = expandedId === order.id;
            const timeAgo = order.validated_at ? formatTimeAgo(order.validated_at) : null;

            return (
              <Card key={order.id} variant="default" padding="md" className="overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleExpand(order.id)}
                  className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-lg"
                  aria-expanded={isExpanded}
                  aria-controls={`delivery-details-${order.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <span className="font-mono font-semibold text-neutral-900 dark:text-neutral-100">
                        {order.order_number}
                      </span>
                      <p className="mt-0.5 font-medium text-neutral-900 dark:text-neutral-100 truncate">
                        {order.customer_name}
                      </p>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {formatDecimal(order.item_count)} item{order.item_count !== 1 ? "s" : ""} —{" "}
                        {formatCurrency(Number(order.total_amount))}
                      </p>
                      {timeAgo && (
                        <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-0.5">
                          {t("delivery_validated_ago", { timeAgo })}
                        </p>
                      )}
                    </div>
                    <span className="shrink-0 text-neutral-400">
                      {isExpanded ? (
                        <ChevronUpIcon className="w-5 h-5" aria-hidden />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5" aria-hidden />
                      )}
                    </span>
                  </div>
                </button>

                {/* Customer contact/location + delivery actions */}
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start">
                  <div className="min-w-0 flex-1 space-y-1.5">
                    {effectiveAddress && (
                      <p className="text-sm text-neutral-700 dark:text-neutral-300 break-words">
                        <span className="font-medium">{t("delivery_address_label")}</span>{" "}
                        {effectiveAddress}
                      </p>
                    )}
                    {effectiveAddressNotes && (
                      <p className="text-sm text-neutral-600 dark:text-neutral-400 break-words">
                        <span className="font-medium">{t("delivery_address_notes_label")}</span>{" "}
                        {effectiveAddressNotes}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-3">
                      {mapsUrl && (
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:underline min-h-[44px] min-w-[44px]"
                        >
                          <MapPinIcon className="w-5 h-5" aria-hidden />
                          <span>{t("delivery_open_maps")}</span>
                        </a>
                      )}
                      {order.customer_phone && (
                        <a
                          href={`tel:${order.customer_phone}`}
                          className="inline-flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:underline min-h-[44px] min-w-[44px]"
                        >
                          <PhoneIcon className="w-5 h-5" aria-hidden />
                          <span>{order.customer_phone}</span>
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end">
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => handleMarkDeliveredClick(order)}
                      disabled={completingId === order.id}
                      className="inline-flex items-center justify-center gap-1.5 w-full sm:w-auto"
                    >
                      <CheckCircleIcon className="w-5 h-5" aria-hidden />
                      {t("btn_mark_delivered")}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => handleReportIssueClick(order)}
                      disabled={submittingIssue && reportIssueOrder?.id === order.id}
                      className="inline-flex items-center justify-center gap-1.5 w-full sm:w-auto"
                    >
                      <ExclamationTriangleIcon className="w-5 h-5" aria-hidden />
                      {t("btn_report_issue")}
                    </Button>
                  </div>
                </div>

                {/* Expanded details */}
                <div
                  id={`delivery-details-${order.id}`}
                  className={`mt-3 pt-3 border-t border-neutral-200 dark:border-neutral-700 ${isExpanded ? "block" : "hidden"}`}
                >
                  <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">
                    {t("delivery_items_label")}
                  </p>
                  <ul className="space-y-1 text-sm text-neutral-700 dark:text-neutral-300">
                    {order.items.map((item) => (
                      <li key={item.id}>
                        {item.product_name} × {formatDecimal(item.quantity)} —{" "}
                        {formatCurrency(Number(item.line_total))}
                      </li>
                    ))}
                  </ul>
                  {order.notes && (
                    <>
                      <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mt-3 mb-1">
                        {t("delivery_notes_label")}
                      </p>
                      <p className="text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">
                        {order.notes}
                      </p>
                    </>
                  )}
                  <p className="mt-3 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                    {t("delivery_total_label")} {formatCurrency(Number(order.total_amount))}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>
      ) : activeTab === "sales" ? (
        <div className="space-y-4">
          {quickSalesTodayLoading ? (
            <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-8 text-center">
              <p className="text-neutral-600 dark:text-neutral-400">{t("sales_loading")}</p>
            </div>
          ) : quickSalesToday.length === 0 ? (
            <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-8 text-center flex flex-col gap-4">
              <p className="text-neutral-600 dark:text-neutral-400 font-medium">
                {t("sales_empty")}
              </p>
              <p className="text-sm text-neutral-500 dark:text-neutral-500">
                {t("sales_empty_hint")}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {quickSalesToday.map((sale) => (
                <Card key={sale.id} variant="default" padding="md">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <span className="font-mono font-semibold text-neutral-900 dark:text-neutral-100">
                        {sale.order_number}
                      </span>
                      <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mt-0.5">
                        {sale.customer_name}
                      </p>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {sale.items
                          .map((i) => `${i.product_name} × ${formatDecimal(i.quantity)}`)
                          .join(", ")}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">
                        {sale.created_at ? new Date(sale.created_at).toLocaleString() : ""}
                      </p>
                    </div>
                    <p className="font-semibold text-neutral-900 dark:text-neutral-100 shrink-0">
                      {formatCurrency(Number(sale.total_amount))}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      ) : activeTab === "unpaid" ? (
        <UnpaidOrdersTab onToast={showToastMessage} />
      ) : null}

      {/* Quick Sale FAB (Story 8-4) */}
      <Button
        type="button"
        variant="primary"
        onClick={openQuickSale}
        className="fixed bottom-6 end-6 rounded-full p-4 shadow-lg min-h-[56px] min-w-[56px] z-40 inline-flex items-center justify-center"
        aria-label="Quick Sale"
      >
        <ShoppingCartIcon className="w-7 h-7" aria-hidden />
      </Button>

      {/* Confirm delivery modal (Story 8-3) */}
      <Modal
        isOpen={!!confirmDelivery}
        onClose={() => {
          if (!completingId) {
            setConfirmDelivery(null);
            setDeliveryNotes("");
          }
        }}
        title={t("confirm_delivery_title")}
        description={
          confirmDelivery
            ? t("confirm_delivery_description", {
                orderNumber: confirmDelivery.order_number,
                customerName: confirmDelivery.customer_name,
              })
            : undefined
        }
        showCloseButton={!completingId}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {confirmDelivery &&
              t("confirm_delivery_notify", { orderNumber: confirmDelivery.order_number })}
          </p>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {t("notes_optional_label")}
          </label>
          <textarea
            value={deliveryNotes}
            onChange={(e) => setDeliveryNotes(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder={t("delivery_notes_placeholder")}
            className="block w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            aria-label="Delivery notes"
            disabled={!!completingId}
          />
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setConfirmDelivery(null);
                setDeliveryNotes("");
              }}
              disabled={!!completingId}
            >
              {t("btn_cancel")}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleConfirmDelivery}
              disabled={!!completingId}
            >
              {completingId ? t("btn_confirming") : t("btn_confirm")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Report issue modal (Story 8-3) */}
      <Modal
        isOpen={!!reportIssueOrder}
        onClose={() => !submittingIssue && setReportIssueOrder(null)}
        title={t("report_issue_title")}
        description={
          reportIssueOrder
            ? t("issue_order_description", {
                orderNumber: reportIssueOrder.order_number,
                customerName: reportIssueOrder.customer_name,
              })
            : undefined
        }
        showCloseButton={!submittingIssue}
        size="md"
      >
        <div className="flex flex-col gap-4">
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {t("issue_reason_label")}
          </label>
          <select
            value={reportIssueReason}
            onChange={(e) => setReportIssueReason(e.target.value as StaffDeliveryIssueReason)}
            className="block w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            aria-label="Issue reason"
          >
            <option value="customer_not_available">
              {t("issue_reason_customer_not_available")}
            </option>
            <option value="wrong_address">{t("issue_reason_wrong_address")}</option>
            <option value="refused_delivery">{t("issue_reason_refused_delivery")}</option>
            <option value="other">{t("issue_reason_other")}</option>
          </select>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {t("notes_optional_label")}
          </label>
          <textarea
            value={reportIssueNotes}
            onChange={(e) => setReportIssueNotes(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder={t("issue_notes_placeholder")}
            className="block w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            aria-label="Issue notes"
          />
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setReportIssueOrder(null)}
              disabled={!!submittingIssue}
            >
              {t("btn_cancel")}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmitReportIssue}
              disabled={!!submittingIssue}
            >
              {submittingIssue ? t("btn_submitting") : t("btn_submit")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Quick Sale modal (Story 8-4) */}
      <Modal
        isOpen={quickSaleOpen}
        onClose={() => !quickSaleSubmitting && setQuickSaleOpen(false)}
        title={t("quick_sale_title")}
        description={t("quick_sale_description")}
        showCloseButton={!quickSaleSubmitting}
        size="2xl"
      >
        <div className="flex flex-col gap-5">
        {/* Customer selector */}
        <div className="w-full">
          <label className="block text-base font-semibold text-neutral-800 dark:text-neutral-200 mb-1">
            {t("qs_customer_label")}
          </label>
          {selectedCustomer ? (
            <div className="flex items-center justify-between gap-2 p-3 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800">
              <div>
                <p className="font-medium text-neutral-900 dark:text-neutral-100">
                  {selectedCustomer.name}
                </p>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 phone-ltr">
                  {selectedCustomer.phone}
                </p>
                {selectedCustomer.address?.trim() && (
                  <p className="text-sm text-neutral-500 dark:text-neutral-500 mt-0.5">
                    {selectedCustomer.address}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setSelectedCustomer(null)}
                disabled={quickSaleSubmitting}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                {t("btn_change")}
              </button>
            </div>
          ) : (
            <div className="relative">
              <MagnifyingGlassIcon className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="search"
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  setCustomerDropdownOpen(true);
                }}
                onFocus={() => setCustomerDropdownOpen(true)}
                placeholder={t("qs_customer_search_placeholder")}
                disabled={customersLoading || quickSaleSubmitting}
                className="w-full ps-10 pe-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 min-h-[44px] disabled:opacity-50"
                aria-label="Search customer"
              />
              {customerDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    aria-hidden
                    onClick={() => setCustomerDropdownOpen(false)}
                  />
                  <div className="absolute z-20 mt-1 w-full rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-lg max-h-60 overflow-y-auto">
                    {/* Walk-in option */}
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCustomer(null);
                        setCustomerSearch("");
                        setCustomerDropdownOpen(false);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-neutral-50 dark:hover:bg-neutral-700/50 border-b border-neutral-200 dark:border-neutral-700 min-h-[44px]"
                    >
                      <p className="font-medium text-neutral-900 dark:text-neutral-100">
                        {t("qs_walkin_title")}
                      </p>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {t("qs_walkin_subtitle")}
                      </p>
                    </button>
                    {/* Create new customer option */}
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateCustomer(true);
                        setCustomerDropdownOpen(false);
                      }}
                      className="w-full px-4 py-3 text-left text-primary-600 dark:text-primary-400 font-medium hover:bg-neutral-50 dark:hover:bg-neutral-700/50 flex items-center gap-2 border-b border-neutral-200 dark:border-neutral-700 min-h-[44px]"
                    >
                      <PlusIcon className="w-5 h-5" /> {t("qs_create_customer")}
                    </button>
                    {/* Customer list */}
                    {customersLoading ? (
                      <div className="px-4 py-8 text-center text-neutral-500">
                        {t("qs_customers_loading")}
                      </div>
                    ) : customers.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <p className="text-neutral-500 dark:text-neutral-400 mb-2">
                          {t("qs_customers_empty")}
                        </p>
                      </div>
                    ) : (
                      <>
                        {customers
                          .filter((c) =>
                            customerSearch.trim()
                              ? c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
                                c.phone.includes(customerSearch)
                              : true
                          )
                          .map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => {
                                setSelectedCustomer(c);
                                setCustomerSearch("");
                                setCustomerDropdownOpen(false);
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-neutral-50 dark:hover:bg-neutral-700/50 min-h-[44px]"
                            >
                              <p className="font-medium text-neutral-900 dark:text-neutral-100">
                                {c.name}
                              </p>
                              <p className="text-sm text-neutral-600 dark:text-neutral-400 phone-ltr">
                                {c.phone}
                              </p>
                              {c.address?.trim() && (
                                <p className="text-sm text-neutral-500 dark:text-neutral-500 truncate">
                                  {c.address}
                                </p>
                              )}
                            </button>
                          ))}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <label className="block text-base font-semibold text-neutral-800 dark:text-neutral-200">
            {t("qs_add_product_label")}
          </label>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
            <select
              defaultValue=""
              onChange={(e) => {
                const id = e.target.value;
                if (!id) return;
                const product = products.find((p) => p.id === id);
                if (product) addQuickSaleLine(product);
                e.target.value = "";
              }}
              disabled={productsLoading || !!quickSaleSubmitting}
              className="w-full sm:min-w-[240px] sm:max-w-md rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 min-h-[44px]"
              aria-label="Add product"
            >
              <option value="">{t("qs_select_product")}</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {formatCurrency(p.unit_price)}
                </option>
              ))}
            </select>
            {lastSelectedProductId &&
              (() => {
                const p = products.find((x) => x.id === lastSelectedProductId);
                const sizes = p?.sales_pack_sizes ?? [];
                if (sizes.length < 1) return null;
                return (
                  <div className="flex flex-wrap items-center gap-2">
                    {sizes.length === 1 ? (
                      <>
                        <button
                          type="button"
                          onClick={() => p && addQuickSaleLine(p, 1)}
                          disabled={!!quickSaleSubmitting}
                          className="px-3 py-2 border border-primary-600 text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 text-sm font-medium min-h-[44px]"
                        >
                          {t("btn_add_1_pack")}
                        </button>
                        <button
                          type="button"
                          onClick={() => p && addQuickSaleLine(p, 5)}
                          disabled={!!quickSaleSubmitting}
                          className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700/50 text-sm font-medium min-h-[44px]"
                        >
                          {t("btn_add_5_packs")}
                        </button>
                      </>
                    ) : (
                      <>
                        {sizes.map((sz) => (
                          <button
                            key={sz}
                            type="button"
                            onClick={() => p && addQuickSaleLine(p, 1, sz)}
                            disabled={!!quickSaleSubmitting}
                            className="px-3 py-2 border border-primary-600 text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 text-sm font-medium min-h-[44px]"
                          >
                            {t("btn_add_1_pack_size", { size: sz })}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => p && addQuickSaleLine(p, 5)}
                          disabled={!!quickSaleSubmitting}
                          className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700/50 text-sm font-medium min-h-[44px]"
                        >
                          {t("btn_add_5_packs")}
                        </button>
                      </>
                    )}
                  </div>
                );
              })()}
          </div>
          {!productsLoading && products.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {t("qs_quick_add_photo")}
              </p>
              <div className="max-h-60 overflow-y-auto rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 border-b last:border-b-0 border-neutral-100 dark:border-neutral-700"
                  >
                    <div className="min-w-0 flex items-center gap-2">
                      <ProductPhoto attachmentId={product.primary_photo_id ?? null} size="md" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                          {product.name}
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          {formatCurrency(product.unit_price)}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => addQuickSaleLine(product)}
                      disabled={!!quickSaleSubmitting}
                      className="min-h-[40px]"
                    >
                      {t("btn_add")}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {quickSaleLines.length > 0 && (
          <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-4 bg-neutral-50/50 dark:bg-neutral-800/30">
            <p className="text-base font-semibold text-neutral-800 dark:text-neutral-200 mb-3">
              {t("delivery_items_label")}
            </p>
            {/* Story 11.4-1: discount mode selector — ABOVE the lines */}
            <div className="mb-3">
              <OrderDiscountControls
                section="selector"
                idPrefix="quicksale"
                discountScope={qsDiscountScope}
                onScopeChange={handleQsScopeChange}
                orderDiscountType={qsOrderDiscountType}
                onOrderDiscountTypeChange={setQsOrderDiscountType}
                orderDiscountValue={qsOrderDiscountValue}
                onOrderDiscountValueChange={setQsOrderDiscountValue}
                discountReason={qsDiscountReason}
                onDiscountReasonChange={setQsDiscountReason}
                totals={quickSaleTotals}
              />
            </div>
            <ul className="space-y-4">
              {quickSaleLines.map((line) => {
                const sizes = line.sales_pack_sizes ?? [];
                const hasPack = sizes.length >= 1;
                const packSize =
                  line.qtyMode === "packs"
                    ? (line.selectedPackSize ?? sizes[0] ?? 1)
                    : (sizes[0] ?? 1);
                const mode = line.qtyMode ?? "units";
                const displayValue =
                  hasPack && mode === "packs"
                    ? Math.floor(line.quantity / packSize)
                    : line.quantity;
                const delta = hasPack && mode === "packs" ? packSize : 1;
                const packCountForHelper =
                  packSize > 0 ? Math.floor(line.quantity / packSize) : 0;

                return (
                  <li
                    key={line.lineKey}
                    className="flex flex-col gap-3 rounded-lg border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <ProductPhoto attachmentId={line.primary_photo_id} size="md" />
                      <p className="text-base font-medium text-neutral-900 dark:text-neutral-100">
                        {line.product_name}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {hasPack && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                            {t("qs_qty_in_label")}
                          </span>
                          <button
                            type="button"
                            onClick={() => setQuickSaleQtyMode(line.lineKey, "units")}
                            disabled={!!quickSaleSubmitting}
                            className={`px-3 py-2 text-sm font-medium rounded-lg border min-h-[40px] ${
                              mode === "units"
                                ? "bg-primary-600 border-primary-600 text-white"
                                : "border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                            }`}
                          >
                            {t("qs_mode_bottles")}
                          </button>
                          <button
                            type="button"
                            onClick={() => setQuickSaleQtyMode(line.lineKey, "packs")}
                            disabled={!!quickSaleSubmitting}
                            className={`px-3 py-2 text-sm font-medium rounded-lg border min-h-[40px] ${
                              mode === "packs"
                                ? "bg-primary-600 border-primary-600 text-white"
                                : "border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                            }`}
                          >
                            {t("qs_mode_packs")}
                          </button>
                        </div>
                      )}
                      {hasPack && mode === "packs" && sizes.length > 1 && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                            {t("qs_pack_size_label")}
                          </span>
                          {sizes.map((sz) => (
                            <button
                              key={sz}
                              type="button"
                              onClick={() => setQuickSalePackSize(line.lineKey, sz)}
                              disabled={!!quickSaleSubmitting}
                              className={`px-3 py-2 text-sm font-medium rounded-lg border min-h-[36px] ${
                                (line.selectedPackSize ?? sizes[0]) === sz
                                  ? "bg-primary-600 border-primary-600 text-white"
                                  : "border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300"
                              }`}
                            >
                              {formatDecimal(sz)}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => adjustQuickSaleQuantity(line.lineKey, -delta)}
                          disabled={quickSaleSubmitting}
                          className="p-2.5 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700 min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
                          aria-label="Decrease quantity"
                        >
                          <MinusIcon className="w-5 h-5" aria-hidden />
                        </button>
                        <input
                          type="number"
                          min={1}
                          value={displayValue}
                          onChange={(e) => {
                            const raw = parseInt(e.target.value, 10) || 0;
                            const units = hasPack && mode === "packs" ? raw * packSize : raw;
                            setQuickSaleQuantity(line.lineKey, Math.max(0, units));
                          }}
                          disabled={!!quickSaleSubmitting}
                          className="w-16 text-center font-medium tabular-nums px-2 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-primary-500 min-h-[44px] text-base"
                          aria-label="Quantity"
                        />
                        <button
                          type="button"
                          onClick={() => adjustQuickSaleQuantity(line.lineKey, delta)}
                          disabled={quickSaleSubmitting}
                          className="p-2.5 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700 min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
                          aria-label="Increase quantity"
                        >
                          <PlusIcon className="w-5 h-5" aria-hidden />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeQuickSaleLine(line.lineKey)}
                          disabled={!!quickSaleSubmitting}
                          className="p-2.5 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
                          aria-label="Remove line"
                        >
                          <TrashIcon className="w-5 h-5" aria-hidden />
                        </button>
                      </div>
                    </div>
                    {/* Editable unit price (per underlying unit) + optional per-line discount */}
                    <div className="flex flex-wrap items-start gap-3">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                            {t("qs_unit_price_label")}
                          </span>
                          <UnitPriceInput
                            line={{
                              lineKey: line.lineKey,
                              catalog_item_id: line.catalog_item_id,
                              name: line.product_name,
                              unit_price: line.unit_price,
                              quantity: line.quantity,
                            }}
                            setUnitPrice={setQuickSaleUnitPrice}
                          />
                        </div>
                        <PriceUnitHint
                          line={{
                            lineKey: line.lineKey,
                            catalog_item_id: line.catalog_item_id,
                            name: line.product_name,
                            unit_price: line.unit_price,
                            quantity: line.quantity,
                            unit_of_measure: line.unit_of_measure,
                            sales_pack_sizes: line.sales_pack_sizes,
                            selectedPackSize: line.selectedPackSize,
                            qtyMode: line.qtyMode,
                          }}
                        />
                      </div>
                      {qsDiscountScope === "line" && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                            {t("qs_discount_label")}
                          </span>
                          <LineDiscountControl
                            line={{
                              lineKey: line.lineKey,
                              catalog_item_id: line.catalog_item_id,
                              name: line.product_name,
                              unit_price: line.unit_price,
                              quantity: line.quantity,
                              discount_type: line.discount_type,
                              discount_value: line.discount_value,
                            }}
                            setLineDiscountType={setQuickSaleLineDiscountType}
                            setLineDiscountValue={setQuickSaleLineDiscountValue}
                          />
                        </div>
                      )}
                    </div>
                    {hasPack &&
                      mode === "packs" &&
                      packSize > 0 &&
                      line.quantity % packSize === 0 && (
                        <span className="text-xs text-neutral-500 dark:text-neutral-400 w-full mt-0.5">
                          {formatDecimal(packCountForHelper)} pack
                          {packCountForHelper !== 1 ? "s" : ""} × {formatDecimal(packSize)} (
                          {formatDecimal(line.quantity)} bottles)
                        </span>
                      )}
                  </li>
                );
              })}
            </ul>
            {/* Story 11.4-1: whole-order (global) discount + reason, then live totals — BELOW the lines */}
            <div className="mt-3">
              <OrderDiscountControls
                section="summary"
                idPrefix="quicksale"
                discountScope={qsDiscountScope}
                onScopeChange={handleQsScopeChange}
                orderDiscountType={qsOrderDiscountType}
                onOrderDiscountTypeChange={setQsOrderDiscountType}
                orderDiscountValue={qsOrderDiscountValue}
                onOrderDiscountValueChange={setQsOrderDiscountValue}
                discountReason={qsDiscountReason}
                onDiscountReasonChange={setQsDiscountReason}
                totals={quickSaleTotals}
              />
            </div>
            <p className="mt-2 text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              {t("qs_total_label")} {formatCurrency(quickSaleTotal)}
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
            {t("notes_optional_label")}
          </label>
          <textarea
            value={quickSaleNotes}
            onChange={(e) => setQuickSaleNotes(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder={t("qs_notes_placeholder")}
            disabled={!!quickSaleSubmitting}
            className="block w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            aria-label="Notes"
          />
        </div>

        {/* Deferred payment (Story 11.6-1): sale on credit — requires a real customer (walk-in disabled) */}
        <div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={qsDeferred}
              onChange={(e) => setQsDeferred(e.target.checked)}
              disabled={!!quickSaleSubmitting}
              className="w-4 h-4 rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500"
              aria-label={t("orders:form_deferred_label")}
            />
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              {t("orders:form_deferred_label")}
            </span>
          </label>
          {qsDeferred && (
            <>
              {!selectedCustomer && (
                <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                  {t("orders:quick_sale_deferred_requires_customer")}
                </p>
              )}
              <div className="mt-2">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  {t("orders:form_payment_due_date_label")}
                </label>
                <input
                  type="date"
                  value={qsPaymentDueDate}
                  onChange={(e) => setQsPaymentDueDate(e.target.value)}
                  disabled={!!quickSaleSubmitting}
                  className="rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </>
          )}
        </div>

        <div className="flex gap-3 justify-end pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => !quickSaleSubmitting && setQuickSaleOpen(false)}
            disabled={!!quickSaleSubmitting}
          >
            {t("btn_cancel")}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleCompleteQuickSale}
            disabled={quickSaleLines.length === 0 || !!quickSaleSubmitting}
          >
            {quickSaleSubmitting ? t("btn_completing") : t("btn_complete_sale")}
          </Button>
        </div>
        </div>
      </Modal>

      {/* No customer warning modal */}
      <Modal
        isOpen={showNoCustomerWarning}
        onClose={() => !quickSaleSubmitting && setShowNoCustomerWarning(false)}
        title={t("no_customer_title")}
        description={t("no_customer_description")}
        showCloseButton={!quickSaleSubmitting}
      >
        <div className="flex flex-col gap-4">
        <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4">
          <div className="flex gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                {t("no_customer_warning_title")}
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                {t("no_customer_warning_text")}
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShowNoCustomerWarning(false)}
            disabled={!!quickSaleSubmitting}
          >
            {t("btn_go_back")}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={async () => {
              await submitQuickSale(buildQuickSaleItems());
              setShowNoCustomerWarning(false);
            }}
            disabled={!!quickSaleSubmitting}
          >
            {quickSaleSubmitting ? t("btn_completing") : t("btn_continue_without_customer")}
          </Button>
        </div>
        </div>
      </Modal>

      {/* Create Customer modal */}
      <Modal
        isOpen={showCreateCustomer}
        onClose={() => !creatingCustomer && setShowCreateCustomer(false)}
        title={t("create_customer_title")}
        description={t("create_customer_description")}
        showCloseButton={!creatingCustomer}
        size="md"
      >
        <CustomerForm
        onSubmit={handleCreateCustomer}
        onCancel={() => !creatingCustomer && setShowCreateCustomer(false)}
        isLoading={creatingCustomer}
        />
      </Modal>

      {/* Toast message */}
      {toast && (
        <div
        className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
          toast.type === "success"
            ? "bg-green-600 text-white dark:bg-green-500"
            : "bg-red-600 text-white dark:bg-red-500"
        }`}
        role="status"
        aria-live="polite"
        >
        {toast.message}
        </div>
      )}
    </div>
  );
}
